import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { ScratchModel } from '../model/scratch/ScratchModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { ScratchGrid } from '../widgets/scratch/ScratchGrid';
import { ScratchHudComponent } from './components/ScratchHudComponent';
import { DEFAULT_SCRATCH_SYMBOLS } from './fixtures/scratchFixtures';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

export interface ScratchDemoSceneOptions {
  /** Called when the player taps CLAIM after a scratch round resolves —
   * the host typically returns the player to the world map. */
  onComplete?: () => void;
}

/**
 * Scratch-card demo — tap each of 9 cells to reveal a symbol; if 3 of any
 * symbol appear on the board, win coins (and on a "big" outcome, gems).
 *
 * <p>Outcome is rolled at construction time and the board is built to
 * match the rolled outcome (see {@link ScratchModel}). Coin + gem credits
 * happen synchronously the moment the model emits {@code scratch-complete}
 * — running the credit before the win animation means a mid-anim canvas
 * teardown (theme switch, route change) doesn't cost the player their reward.
 */
export class ScratchDemoScene extends SceneBase {
  private grid: ScratchGrid | null = null;
  private hud: ScratchHudComponent | null = null;
  private model: ScratchModel | null = null;
  private readonly random: Random;
  private readonly profile: PlayerProfile;
  private readonly onComplete: (() => void) | null;
  private completionSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile,
    options: ScratchDemoSceneOptions = {},
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.profile = profile;
    this.onComplete = options.onComplete ?? null;
  }

  protected buildContent(): void {
    this.model = new ScratchModel(
      {
        symbols: DEFAULT_SCRATCH_SYMBOLS,
        cellCount: 9,
        columnCount: 3,
      },
      this.random,
    );

    this.grid = new ScratchGrid({
      cellCount: this.model.cellCount,
      columnCount: this.model.columnCount,
      foilColor: this.theme.palette.accent,
      onPick: (index) => void this.handlePick(index),
    });
    this.app.stage.addChild(this.grid);

    this.hud = new ScratchHudComponent(
      this.model,
      this.profile,
      { onClaim: () => this.handleClaim() },
      this.theme.palette.accent,
    );
    this.app.stage.addChild(this.hud);

    // Credit reward synchronously when the round completes — see class
    // doc for why this runs on event rather than after animation.
    this.completionSubscription = this.model.events.on('scratch-complete', (event) => {
      if (event.coins > 0) this.profile.credit(event.coins);
      if (event.gems > 0) this.profile.awardGems(event.gems);
    });

    this.layout();
  }

  protected onResize(): void {
    this.layout();
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.completionSubscription?.dispose();
    this.completionSubscription = null;
    this.model?.dispose();
    this.model = null;
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    if (this.grid) {
      this.grid.destroyGrid();
      this.grid.destroy({ children: true });
      this.grid = null;
    }
  }

  private layout(): void {
    if (!this.grid || !this.hud) return;
    const { width, height } = this.app.renderer;
    const naturalWidth = this.grid.totalWidth();
    const naturalHeight = this.grid.totalHeight();
    const horizontalGutter = 24;
    const scale = Math.min(1, (width - horizontalGutter * 2) / naturalWidth);
    this.grid.scale.set(scale);
    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;
    const gridX = Math.round((width - scaledWidth) / 2);
    const gridY = Math.round((height - scaledHeight) / 2 - 56 + 16);
    this.grid.x = gridX;
    this.grid.y = gridY;

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: gridY,
      machineBottom: gridY + scaledHeight,
      spinButtonY: Math.round(gridY + scaledHeight + 70),
    };
    this.hud.layout(ctx);
  }

  private async handlePick(index: number): Promise<void> {
    if (!this.model || !this.grid) return;
    const symbol = this.model.reveal(index);
    if (!symbol) return;
    await this.grid.reveal(index, symbol);
  }

  private handleClaim(): void {
    this.onComplete?.();
  }
}
